/**
 * Migrate v1 workflow format (flat nodes/edges) to v2 (XState machine config).
 *
 * v1 format (current):
 *   { id, name, domain, cadence, regulatorySource, nodes: [{id, type, position, label, ...}], edges: [{id, from, to, ...}] }
 *
 * v2 format (new):
 *   { id, name, version, domain, cadence, regulatorySource, swimlanes, machine: { id, initial, context, states }, triggers, guards, actions }
 */

/**
 * Detect whether a workflow object is v1 or v2 format.
 */
export function detectWorkflowVersion(workflow) {
  if (workflow.machine && workflow.machine.states) return 2;
  if (workflow.nodes && Array.isArray(workflow.nodes)) return 1;
  return 0; // unknown
}

/**
 * Convert a v1 workflow (flat nodes/edges) to v2 (XState machine config).
 * Preserves all visual metadata in state.meta.
 */
export function migrateV1ToV2(v1) {
  const nodes = v1.nodes || [];
  const edges = v1.edges || [];

  // Build adjacency map
  const outgoing = {};
  edges.forEach((e) => {
    const from = e.from || e.source;
    if (!outgoing[from]) outgoing[from] = [];
    outgoing[from].push(e);
  });

  // Find start node (no incoming edges, not an end node)
  const incoming = new Set(edges.map((e) => e.to || e.target));
  const startCandidates = nodes.filter((n) => !incoming.has(n.id) && n.type !== 'end');
  const startNode = startCandidates[0] || nodes.find((n) => n.type === 'task') || nodes[0];

  // Build states
  const states = {};
  nodes.forEach((node) => {
    const stateConfig = {};

    // Meta (visual info)
    stateConfig.meta = {
      nodeType: node.type || 'task',
      label: node.label || node.id,
      ...(node.completionType && { completionType: node.completionType }),
      ...(node.estimatedMinutes && { estimatedMinutes: node.estimatedMinutes }),
      ...(node.domain && { domain: node.domain }),
      ...(node.taskId && { taskId: node.taskId }),
      ...(node.options && { options: node.options }),
    };

    // Final states
    if (node.type === 'end') {
      stateConfig.type = 'final';
      states[node.id] = stateConfig;
      return;
    }

    // Transitions
    const nodeEdges = outgoing[node.id] || [];

    if (node.type === 'gate') {
      // Gate: use `always` with guard-like labels
      if (nodeEdges.length > 0) {
        stateConfig.always = nodeEdges.map((e) => ({
          target: e.to || e.target,
          ...(e.fromPort && { guard: e.fromPort }),
          ...(e.label && { guard: e.label }),
        }));
      }
    } else if (node.type === 'join') {
      // Join: transitions to next after all incoming are satisfied
      if (nodeEdges.length > 0) {
        stateConfig.on = {
          CONTINUE: { target: (nodeEdges[0].to || nodeEdges[0].target) },
        };
      }
    } else {
      // Task or other: standard COMPLETE/SKIP transitions
      const on = {};
      nodeEdges.forEach((e, i) => {
        const target = e.to || e.target;
        const eventName = e.isLoop ? 'LOOP' : (e.label || (i === 0 ? 'COMPLETE' : `PATH_${i}`));
        on[eventName] = { target };
      });
      if (Object.keys(on).length) stateConfig.on = on;
    }

    states[node.id] = stateConfig;
  });

  return {
    id: v1.id,
    name: v1.name,
    version: 1,
    domain: v1.domain || 'operations',
    cadence: v1.cadence || 'as_needed',
    regulatorySource: v1.regulatorySource,

    swimlanes: [],

    machine: {
      id: v1.id || 'workflow',
      initial: startNode?.id || 'start',
      context: {},
      states,
    },

    triggers: [],
    guards: {},
    actions: {},
  };
}

/**
 * Convert a legacy checklist to v2 workflow format.
 * Checklists are linear: step1 → step2 → ... → end.
 */
export function migrateChecklistToV2(checklist) {
  const steps = checklist.steps || [];
  const states = {};

  steps.forEach((step, i) => {
    const nextId = i < steps.length - 1 ? steps[i + 1].id : `${checklist.id}-end`;
    states[step.id] = {
      meta: {
        nodeType: 'task',
        label: step.description || step.name || step.id,
        completionType: step.requiresNote ? 'checkbox_note' : 'checkbox',
        domain: checklist.domain,
      },
      on: {
        COMPLETE: { target: nextId },
        SKIP: { target: nextId },
      },
    };
  });

  states[`${checklist.id}-end`] = {
    type: 'final',
    meta: { nodeType: 'end', label: 'Complete' },
  };

  const initial = steps.length > 0 ? steps[0].id : `${checklist.id}-end`;

  return {
    id: checklist.id,
    name: checklist.name,
    version: 1,
    domain: checklist.domain || 'operations',
    cadence: checklist.cadence || 'as_needed',
    regulatorySource: checklist.regulatorySource,

    swimlanes: [],

    machine: {
      id: checklist.id,
      initial,
      context: {},
      states,
    },

    triggers: [],
    guards: {},
    actions: {},
  };
}

/**
 * Ensure a workflow is in v2 format. If v1, auto-migrate.
 */
export function ensureV2(workflow) {
  const version = detectWorkflowVersion(workflow);
  if (version === 2) return workflow;
  if (version === 1) return migrateV1ToV2(workflow);
  // Unknown — treat as v1 best-effort
  return migrateV1ToV2(workflow);
}
